import type { EChartsOption } from 'echarts'
import type { TooltipComponentFormatterCallbackParams } from 'echarts'
import type { ParsedTable, ChartGeneratorSettings } from '../types'
import {
  toNumber,
  axisPercentFormatter,
  resolveSeriesColors,
  GeneratorError,
  getChartColorByIndex,
  lineChartLegend,
  CHART_GRID_TOP_WITH_LEGEND,
  CHART_GRID_BOTTOM,
} from './helpers';
import { filterTableData } from '../utils/chartFilters';

/**
 * Generates ECharts options for a Line chart
 *
 * Assumes:
 * - Column headers (excluding the first) represent the X-axis categories (e.g., time periods).
 * - The first column of each row represents the series name (e.g., brand/category).
 * - Subsequent columns contain the data points for that series.
 *
 * @param t - The parsed table data
 * @param title - The title for the chart
 * @param settings - Optional settings (selectedRows, selectedColumns, dataType)
 * @returns ECharts option configuration object for a line chart
 * @throws {GeneratorError} If the table structure is unsuitable or filtering removes all data
 */
export function lineOptions(
  t: ParsedTable,
  title: string,
  settings?: ChartGeneratorSettings
): EChartsOption {

  console.log('--------------------------------------------')
  console.log('lineOptions', t)
  console.log('settings', settings)
  // Apply filtering
  const filteredTable = filterTableData(
    t,
    settings?.selectedRows,
    settings?.selectedColumns
  )

  // Extract data
  const categories = filteredTable.headers.slice(1) // X-axis categories (columns)
  const seriesNames = filteredTable.rows.map((row) => row[0] || 'Unnamed Series')

  const gradientMap = settings?.gradientMap

  // Build series data
  const series: echarts.LineSeriesOption[] = filteredTable.rows.map((row, index) => {
    const seriesName = seriesNames[index]
    const rowData = categories.map((_, i) => {
      const dataIndex = i + 1 // Skip row label
      return dataIndex < row.length ? toNumber(row[dataIndex]) : null
    })

    // Look up gradient or color for this series
    // Uses deterministic color generation for unknown codes to ensure consistency
    const key = seriesName.toLowerCase();
    const gradient = gradientMap?.[key];
    const color = settings?.colorMap?.[key] || getChartColorByIndex(index);

    return {
      name: seriesName,
      type: 'line',
      data: rowData,
      emphasis: {
        focus: 'series',
      },
      lineStyle: {
        width: 3,
        color: gradient || color,
      },
      itemStyle: {
        color: gradient || color,
      },
      // No area fill - lines only
    }
  })

  // Validate we have numeric data
  const hasData = series.some((s) => s.data?.some((d) => d !== null && !isNaN(d as number)))
  if (!hasData) {
    throw new GeneratorError('No valid numeric data found after filtering.')
  }

  return {
    title: {
      text: title,
      left: 'left',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      // One point per hover (#968 comment 4). `trigger: 'axis'` listed every
      // series at the hovered x-position in one box that grew with the series
      // count, anchored high, and was clipped off the top of the chart — so it
      // was standing in for the legend below, which the footer geometry hid.
      // The legend is now visible, and the tooltip answers one question.
      trigger: 'item',
      // Belt and braces against the clipping Matt saw: keep the box inside the
      // chart even when the hovered point sits near an edge.
      confine: true,
      formatter: (params: TooltipComponentFormatterCallbackParams) => {
        // `trigger: 'item'` always passes a single object. The array shape is
        // the axis trigger this chart no longer uses.
        if (Array.isArray(params)) {
          return ''
        }
        const item = params as Exclude<TooltipComponentFormatterCallbackParams, unknown[]>
        const value = typeof item.value === 'number' ? item.value : null
        const seriesName = item.seriesName || 'Unknown'
        const formattedValue =
          value !== null
            ? settings?.dataType === 'freq'
              ? value.toLocaleString()
              : `${(value * 100).toFixed(1)}%`
            : 'N/A'
        return `${item.name} · ${seriesName} · ${formattedValue}`
      },
    },
    // Scroll pagination + hover tooltip for many-series charts (#1184).
    legend: lineChartLegend(seriesNames),
    grid: {
      left: '3%',
      right: '4%',
      top: CHART_GRID_TOP_WITH_LEGEND,
      // #1172: the unconditional dataZoom slider is gone — the range slider
      // is now an opt-in Chart Design setting (x_axis.range_slider), applied
      // by applyChartDesign, which also restores the with-zoom grid bottom.
      bottom: CHART_GRID_BOTTOM,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        // interval deliberately unset (#1178): ECharts' own 'auto' thins
        // colliding labels — 25 months no longer smear into one band. The
        // old force-every-label look is the "Every label" setting.
        rotate: 30,
        fontSize: 10,
      },
      boundaryGap: false,
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: settings?.dataType === 'freq' ? undefined : axisPercentFormatter,
      },
      min: 0,
    },
    series: series,
    color: resolveSeriesColors(seriesNames, settings?.colorMap),
  }
}
